import { computed, ref, type Ref, type InjectionKey } from 'vue'
import { useLocalStorage } from '@vueuse/core'

export interface ProductTourStep {
  id: string
  order: number
  element?: Element
}

export interface ProductTourState {
  isActive: Ref<boolean>
  currentStepIndex: Ref<number>
  totalSteps: Ref<number>
  steps: Ref<ProductTourStep[]>
  currentStep: Ref<ProductTourStep | null>
  isFirstStep: Ref<boolean>
  isLastStep: Ref<boolean>
  goToStep: (stepId: string) => void
  nextStep: () => void
  previousStep: () => void
  startTour: () => void
  completeTour: () => void
  skipTour: () => void
  registerStep: (step: ProductTourStep) => void
  unregisterStep: (stepId: string) => void
  updateStepElement: (stepId: string, element: Element | null) => void
  isStepActive: (stepId: string) => boolean
}

export const productTourInjectionKey: InjectionKey<ProductTourState> =
  Symbol('productTour')

/**
 * Composable for managing product tours
 * @param tourKey - Unique key for localStorage persistence
 * @param autoStart - Whether to auto-start the tour if not completed
 */
export function useProductTour(tourKey: string, autoStart = true) {
  const hasCompletedTour = useLocalStorage(
    `product-tour-${tourKey}-completed`,
    false,
  )
  const isActive = ref(!hasCompletedTour.value && autoStart)
  const currentStepIndex = ref(0)
  const steps = ref<ProductTourStep[]>([])

  const totalSteps = computed(() => steps.value.length)

  const currentStep = computed(() => {
    if (
      currentStepIndex.value >= 0 &&
      currentStepIndex.value < steps.value.length
    ) {
      return steps.value[currentStepIndex.value]
    }
    return null
  })

  const isFirstStep = computed(() => currentStepIndex.value === 0)
  const isLastStep = computed(
    () => currentStepIndex.value === totalSteps.value - 1,
  )

  function registerStep(step: ProductTourStep) {
    const existingIndex = steps.value.findIndex((s) => s.id === step.id)
    if (existingIndex === -1) {
      steps.value.push(step)
      // Sort steps by order
      steps.value.sort((a, b) => a.order - b.order)
    }
  }

  function unregisterStep(stepId: string) {
    const index = steps.value.findIndex((s) => s.id === stepId)
    if (index !== -1) {
      steps.value.splice(index, 1)
      // Adjust current step index if necessary
      if (currentStepIndex.value >= index && currentStepIndex.value > 0) {
        currentStepIndex.value--
      }
    }
  }

  function updateStepElement(stepId: string, element: Element | null) {
    const step = steps.value.find((s) => s.id === stepId)
    if (step) {
      step.element = element || undefined
    }
  }

  function goToStep(stepId: string) {
    const index = steps.value.findIndex((s) => s.id === stepId)
    if (index !== -1) {
      currentStepIndex.value = index
    }
  }

  function nextStep() {
    if (!isLastStep.value) {
      currentStepIndex.value++
    } else {
      completeTour()
    }
  }

  function previousStep() {
    if (!isFirstStep.value) {
      currentStepIndex.value--
    }
  }

  function startTour() {
    isActive.value = true
    currentStepIndex.value = 0
  }

  function completeTour() {
    isActive.value = false
    hasCompletedTour.value = true
  }

  function skipTour() {
    isActive.value = false
    hasCompletedTour.value = true
  }

  function isStepActive(stepId: string) {
    return isActive.value && currentStep.value?.id === stepId
  }

  const tourState: ProductTourState = {
    isActive,
    currentStepIndex,
    totalSteps,
    steps,
    currentStep,
    isFirstStep,
    isLastStep,
    goToStep,
    nextStep,
    previousStep,
    startTour,
    completeTour,
    skipTour,
    registerStep,
    unregisterStep,
    updateStepElement,
    isStepActive,
  }

  return tourState
}
